import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'

const PipelineFormContext = createContext({})

export const usePipelineForm = () => useContext(PipelineFormContext)

function stableStringify (obj) {
  if (obj === null || obj === undefined) return JSON.stringify(obj)
  if (typeof obj !== 'object') return JSON.stringify(obj)
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`
  const keys = Object.keys(obj).sort()
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`
}

function PipelineFormProvider ({ initialValue, children }) {
  const [pipelineId] = useState(initialValue.pipelineId || null)
  const [displayName, setDisplayName] = useState(initialValue.displayName)
  const [code, setCode] = useState(initialValue.code)
  const [description, setDescription] = useState(initialValue.description)
  const [scope, setScope] = useState(initialValue.scope)
  const [validation, setValidation] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const savedSnapshotRef = useRef(stableStringify({
    displayName: initialValue.displayName,
    code: initialValue.code,
    description: initialValue.description,
    scope: initialValue.scope
  }))

  const validate = useCallback(() => {
    const result = {}
    if (!displayName) {
      result.displayName = 'Required'
    } else if (displayName?.length > 64) {
      result.displayName = 'Maximum 64 Characters'
    }

    if (!code) {
      result.code = 'Required'
    } else if (code.length > 64) {
      result.code = 'Maximum 64 Characters'
    } else if (!(/^[a-z0-9_-]+$/.test(code))) {
      result.code = 'Only lowercase letters, numbers, dashes, underscores allowed'
    }

    if (!scope) {
      result.scope = 'Required'
    }

    if (isEmpty(result)) {
      setValidation(null)
      return null
    }

    setValidation(result)
    return result
  }, [displayName, code, scope])

  const isFormDirty = useMemo(() => {
    const current = stableStringify({ displayName, code, description, scope })
    return current !== savedSnapshotRef.current
  }, [displayName, code, description, scope])

  const markFormClean = useCallback(() => {
    savedSnapshotRef.current = stableStringify({ displayName, code, description, scope })
  }, [displayName, code, description, scope])

  const resetFormFields = useCallback(({ displayName: dn, code: c, description: d, scope: s }) => {
    setDisplayName(dn)
    setCode(c)
    setDescription(d)
    setScope(s)
    savedSnapshotRef.current = stableStringify({ displayName: dn, code: c, description: d, scope: s })
    setValidation(null)
  }, [])

  const value = useMemo(() => ({
    pipelineId,
    displayName,
    setDisplayName,
    code,
    setCode,
    description,
    setDescription,
    scope,
    setScope,
    validation,
    validate,
    processing,
    setProcessing,
    submitError,
    setSubmitError,
    isFormDirty,
    markFormClean,
    resetFormFields
  }), [
    pipelineId,
    displayName, code, description, scope,
    validation, validate,
    processing, submitError,
    isFormDirty, markFormClean,
    resetFormFields
  ])

  return (
    <PipelineFormContext.Provider value={value}>
      {children}
    </PipelineFormContext.Provider>
  )
}

PipelineFormProvider.propTypes = {
  initialValue: PropTypes.object.isRequired,
  children: PropTypes.node
}

export default PipelineFormProvider
